import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  DollarSign, 
  TrendingUp, 
  CreditCard, 
  Wallet, 
  BarChart3, 
  PiggyBank,
  ExternalLink,
  Calendar,
  Users,
  Activity
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { CustomBadge } from '@/components/ui/custom-badge';
import { supabase } from '@/integrations/supabase/client';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from 'recharts';

const AdminFinancialDashboard: React.FC = () => {
  const [financialOverview, setFinancialOverview] = useState<any>(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        const [overviewResponse, analyticsResponse] = await Promise.all([
          supabase.functions.invoke('get-admin-financial-overview'),
          supabase.functions.invoke('get-admin-revenue-analytics')
        ]);

        if (overviewResponse.error) {
          console.error('Error fetching financial overview:', overviewResponse.error);
        } else {
          setFinancialOverview(overviewResponse.data);
        }

        if (analyticsResponse.error) {
          console.error('Error fetching revenue analytics:', analyticsResponse.error);
        } else {
          setRevenueAnalytics(analyticsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching financial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFinancialData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStatCard = (title: string, value: string, icon: React.ReactNode, description?: string, trend?: string) => (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-sm font-medium text-gray-500">{title}</CardTitle>
        <div className="flex-shrink-0">{icon}</div>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-2xl font-bold">
          {isLoading ? <Skeleton className="h-8 w-24" /> : value}
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}
        {trend && !isLoading && (
          <p className="text-xs text-green-600 flex items-center">
            <TrendingUp className="h-3 w-3 mr-1" />
            {trend}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Financial Dashboard</h2>
          <p className="text-muted-foreground">
            Platform revenue analytics and financial insights
          </p>
        </div>
        <Button variant="outline" onClick={() => window.open('https://dashboard.stripe.com', '_blank')}>
          <ExternalLink className="h-4 w-4 mr-2" />
          Open Stripe Dashboard
        </Button>
      </div>

      {/* Financial Overview Cards */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {renderStatCard(
          "Total Platform Revenue",
          formatCurrency(financialOverview?.totalPlatformRevenue || 0),
          <DollarSign className="h-4 w-4 text-green-500" />,
          "All-time revenue across platform"
        )}
        {renderStatCard(
          "Monthly Revenue",
          formatCurrency(financialOverview?.monthlyRevenue || 0),
          <TrendingUp className="h-4 w-4 text-blue-500" />,
          "This month's revenue",
          `${formatCurrency(financialOverview?.weeklyRevenue || 0)} this week`
        )}
        {renderStatCard(
          "Platform Commission",
          formatCurrency(financialOverview?.totalPlatformCommission || 0),
          <CreditCard className="h-4 w-4 text-purple-500" />,
          `${financialOverview?.platformCommissionRate || 0}% commission rate`
        )}
        {renderStatCard(
          "Practitioner Earnings",
          formatCurrency(financialOverview?.totalPractitionerEarnings || 0),
          <Wallet className="h-4 w-4 text-amber-500" />,
          "Total paid to practitioners"
        )}
      </div>

      {/* Revenue Trends Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Revenue Trends (Last 12 Months)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-80 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueAnalytics?.monthlyTrends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), '']}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Total Revenue"
                />
                <Line 
                  type="monotone" 
                  dataKey="commission" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  name="Platform Commission"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        {/* Service Revenue Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <PiggyBank className="h-5 w-5 mr-2" />
              Revenue by Service Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={revenueAnalytics?.serviceRevenueDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="service" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => [formatCurrency(value), 'Revenue']} />
                  <Bar dataKey="revenue" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top Performing Practitioners */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Top Performing Practitioners
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Revenue</TableHead>
                    <TableHead className="text-xs">Bookings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {financialOverview?.topPractitioners?.slice(0, 5).map((practitioner: any) => (
                    <TableRow key={practitioner.id}>
                      <TableCell className="font-medium text-xs">{practitioner.name}</TableCell>
                      <TableCell className="text-xs">{formatCurrency(practitioner.totalRevenue)}</TableCell>
                      <TableCell className="text-xs">{practitioner.totalBookings || 0}</TableCell>
                    </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-xs">No data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Practitioner Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Practitioner Performance Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Practitioner</TableHead>
                  <TableHead>Total Revenue</TableHead>
                  <TableHead>Total Bookings</TableHead>
                  <TableHead>Avg Booking Value</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Earnings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revenueAnalytics?.practitionerPerformance?.slice(0, 10).map((practitioner: any) => (
                  <TableRow key={practitioner.id}>
                    <TableCell className="font-medium">{practitioner.name}</TableCell>
                    <TableCell>{formatCurrency(practitioner.totalRevenue)}</TableCell>
                    <TableCell>{practitioner.totalBookings}</TableCell>
                    <TableCell>{formatCurrency(practitioner.averageBookingValue)}</TableCell>
                    <TableCell>{formatCurrency(practitioner.totalRevenue * (financialOverview?.platformCommissionRate || 10) / 100)}</TableCell>
                    <TableCell>{formatCurrency(practitioner.totalRevenue * (100 - (financialOverview?.platformCommissionRate || 10)) / 100)}</TableCell>
                  </TableRow>
                )) || (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">No practitioner data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Analytics Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Analytics Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg">Average Monthly Revenue</h3>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(revenueAnalytics?.summary?.averageMonthlyRevenue || 0)}
                </p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg">Top Performing Service</h3>
                <p className="text-lg font-bold text-blue-600">
                  {revenueAnalytics?.summary?.topPerformingService || 'N/A'}
                </p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg">Top Performer</h3>
                <p className="text-lg font-bold text-purple-600">
                  {revenueAnalytics?.summary?.topPerformingPractitioner || 'N/A'}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFinancialDashboard;